import { useAtom, useAtomValue } from "jotai";
import * as React from "react";
import { Navigate } from "react-router";

import { authDataAtom, authTokenValidatedAtom, jwtDataAtom } from "../atoms";
import { useRefreshToken } from "../hooks/use-refresh-token";
import type { AuthData } from "../types";
import { getLoginRedirectUrl } from "../utils/login-redirect";
import { AuthBootstrap } from "./auth-bootstrap";

const REFRESH_INTERVAL = 1000 * 60 * 30; // 30 min

export function AuthGuard({ children }: React.PropsWithChildren) {
  const [authTokenValidated] = useAtom(authTokenValidatedAtom);

  // AuthData is synced across tabs. This makes logout occur in all tabs
  const [authData] = useAtom(authDataAtom);

  return authData && authTokenValidated ? (
    <AuthSuccess authData={authData}>{children}</AuthSuccess>
  ) : (
    <AuthPending authData={authData}>{children}</AuthPending>
  );
}

interface AuthProps {
  authData: AuthData | null;
  children: React.ReactNode;
}

function AuthPending({ authData }: AuthProps) {
  const jwtPayload = useAtomValue(jwtDataAtom);
  const [, setTokenValidated] = useAtom(authTokenValidatedAtom);

  // Guards "a refresh is in flight", NOT "the effect has run once" — it must never latch on a run
  // that dispatched nothing. `shouldRefresh` is false on the first commit whenever the stored access
  // token is already expired (what a long-backgrounded tab boots with) and true on a later one, since
  // `authDataAtom` syncs on the cross-tab `storage` event and any other open tab landing a fresh
  // token rewrites it mid-mount. The old latch was spent on that no-op run, and because nothing else
  // sets `authTokenValidatedAtom` the app was stranded here for good (B-1927). Re-arming can't loop:
  // success unmounts this branch, and failure nulls `authData` and falls through to the redirect.
  const refreshInFlight = React.useRef(false);
  const settleRefresh = () => {
    refreshInFlight.current = false;
  };
  const [refreshToken] = useRefreshToken({ onCompleted: settleRefresh, onError: settleRefresh });

  const shouldRefresh = Boolean(
    authData?.token.refreshToken && jwtPayload && authData.shouldRemember && !jwtPayload.hasExpired
  );

  const impersonationMode = jwtPayload?.impersonatedById;

  React.useEffect(() => {
    if (!refreshInFlight.current && shouldRefresh && authData?.token.refreshToken) {
      refreshInFlight.current = true;

      refreshToken({
        variables: {
          token: {
            accessToken: authData.token.accessToken,
            refreshToken: authData.token.refreshToken,
          },
        },
      });
    }

    if (impersonationMode) {
      void setTokenValidated(true);
    }
  }, [shouldRefresh, authData, impersonationMode, refreshToken, setTokenValidated]);

  return shouldRefresh || impersonationMode ? <AuthBootstrap /> : <Navigate to={getLoginRedirectUrl()} />;
}

function AuthSuccess({ authData, children }: AuthProps) {
  const [refreshToken] = useRefreshToken();
  // const ldClient = useLDClient();

  React.useEffect(() => {
    if (!authData?.token.refreshToken) {
      return;
    }
    const token = {
      accessToken: authData.token.accessToken,
      refreshToken: authData.token.refreshToken,
    };

    const intervalId = setInterval(() => {
      refreshToken({ variables: { token } });
    }, REFRESH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [refreshToken, authData]);

  return <>{children}</>;
}
